// =============================================================================
// Admin Analytics — feature provider (PROA-1474)
// =============================================================================
// Owns the global filter state (date range w/ comparison + template type), the
// active view tab, and the Activation data adapter. The Reports tab reads the
// `adminAnalytics` GraphQL resource directly (see `data/hooks.ts`); the adapter
// prop only backs Activation — `router.tsx` passes in
// `useGraphqlAdminAnalyticsAdapter()` for the live route, tests pass
// `MockAdminAnalyticsAdapter` (the default).
// =============================================================================
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { useFlags } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from "react";

import { CalendarDateRange } from "@fronterahealth/frontera-ui-components";

import { mockAdminAnalyticsAdapter } from "@pages/AdminAnalytics/data/mockAdapter";
import { AdminAnalyticsAdapter, DEFAULT_ORG_CONFIG, OrgConfig, TemplateType } from "@pages/AdminAnalytics/data/types";
import { defaultCalendarRange } from "@pages/AdminAnalytics/helpers";
import { useAdminData } from "@providers/AdminDataProvider";

export type AdminAnalyticsTab = "Reports" | "Activation";

interface AdminAnalyticsContextI {
  adapter: AdminAnalyticsAdapter;
  /** Global date range (with optional comparison period) from the sticky filter bar. */
  globalRange: CalendarDateRange | undefined;
  setGlobalRange: (range: CalendarDateRange) => void;
  /** Global IA/RA/DB filter. Empty = all. Ignored by the Activation view. */
  templateType: TemplateType[];
  setTemplateType: (template: TemplateType[]) => void;
  /** Global named-template filter. Empty = all. Ignored by the Activation view. */
  templateName: string[];
  setTemplateName: (name: string[]) => void;
  selectedTab: AdminAnalyticsTab;
  setSelectedTab: (tab: AdminAnalyticsTab) => void;
  /** Org-level product config — which templates are enabled and whether the Catalight pipeline is active. */
  orgConfig: OrgConfig;
  /**
   * The org details `orgConfig` is derived from have loaded (or failed). Until
   * then `orgConfig` is the default, so analytics requests wait — otherwise the
   * first one runs with the wrong scope and a second follows once it resolves.
   */
  orgReady: boolean;
}

const AdminAnalyticsContext = createContext<AdminAnalyticsContextI | undefined>(undefined);

export const useAdminAnalytics = (): AdminAnalyticsContextI => {
  const ctx = useContext(AdminAnalyticsContext);
  if (!ctx) throw new Error("useAdminAnalytics must be used within an AdminAnalyticsProvider");
  return ctx;
};

interface AdminAnalyticsProviderProps extends PropsWithChildren {
  /** Defaults to the in-browser mock. Pass a network-backed adapter in production. */
  adapter?: AdminAnalyticsAdapter;
}

export const AdminAnalyticsProvider: React.FC<AdminAnalyticsProviderProps> = ({
  adapter = mockAdminAnalyticsAdapter,
  children,
}) => {
  const { isCatalightUser, isABCUser, orgnizationDetailsQuery } = useAdminData();
  const orgReady = orgnizationDetailsQuery.isSuccess || orgnizationDetailsQuery.isError;
  const { "report-submit-to-payor": hasSubmissionIntegration } = useFlags<FeatureFlags>();

  const orgConfig = useMemo((): OrgConfig => {
    if (isABCUser) return { enabledTemplates: ["DB"], isCatalightOrg: false, hasSubmissionIntegration };
    if (isCatalightUser)
      return { enabledTemplates: ["IA", "RA", "DB"], isCatalightOrg: true, hasSubmissionIntegration };
    return { ...DEFAULT_ORG_CONFIG, hasSubmissionIntegration };
  }, [isABCUser, isCatalightUser, hasSubmissionIntegration]);

  const [globalRange, setGlobalRange] = useState<CalendarDateRange | undefined>(defaultCalendarRange);
  const [templateType, setTemplateType] = useState<TemplateType[]>([]);
  const [templateName, setTemplateName] = useState<string[]>([]);
  // Reset user-driven filter selections when org identity changes so stale
  // selections from a prior org don't bleed into a newly resolved org view.
  // Org-level query scoping (e.g. ABC → DB only) is handled in hooks.ts.
  useEffect(() => {
    setTemplateType([]);
    setTemplateName([]);
  }, [isABCUser, isCatalightUser]);
  const [selectedTab, setSelectedTab] = useState<AdminAnalyticsTab>("Reports");

  const value = useMemo(
    () => ({
      adapter,
      globalRange,
      setGlobalRange,
      templateType,
      setTemplateType,
      templateName,
      setTemplateName,
      selectedTab,
      setSelectedTab,
      orgConfig,
      orgReady,
    }),
    [adapter, globalRange, templateType, templateName, selectedTab, orgConfig, orgReady],
  );

  return <AdminAnalyticsContext.Provider value={value}>{children}</AdminAnalyticsContext.Provider>;
};
